import { useMemo, useRef, useState, type KeyboardEvent } from "react";

import type { LoomAuthoringDocument, LoomPrimitiveDefinition } from "./loomAuthoringModel";
import {
  deriveLoomSceneHierarchy,
  type LoomSceneHierarchyItem,
} from "./loomSceneHierarchy";

interface LoomPatternTreeProps {
  document: LoomAuthoringDocument;
  onSelect: (nodeId: string) => void;
  selectedNodeId: string;
}

interface LoomVisibleTreeItem {
  item: LoomSceneHierarchyItem;
  parentPath: string | null;
}

function primitiveSummary(definition: LoomPrimitiveDefinition): string {
  if (definition.primitive === "sphere") return `r ${definition.radiusM.toFixed(2)} m`;
  if (definition.primitive === "box") return definition.dimensionsM.map((value) => value.toFixed(2)).join(" × ");
  if (definition.primitive === "ellipsoid") return definition.radiiM.map((value) => value.toFixed(2)).join(" / ");
  if (definition.primitive === "pod") return `${definition.lengthM.toFixed(1)} m · FR ${definition.finenessRatio.toFixed(1)}`;
  if (definition.primitive === "wing") return `${definition.panels.length} panel · ${definition.panels[definition.panels.length - 1]?.yTipM.toFixed(1)} m`;
  if (definition.primitive === "fuselage") return `${definition.stations.length} stations`;
  if (definition.primitive === "extrusion") return `${definition.halfLengthM * 2} m ${definition.plane}`;
  return `${definition.axis} axis · ${definition.radialOffsetM.toFixed(2)} m`;
}

function nodeSummary(item: LoomSceneHierarchyItem): string {
  const node = item.node;
  if (!node) return "missing graph input";
  if (item.referenceKind === "shared-reference") return `linked reference · first at ${item.firstPath}`;
  if (item.referenceKind === "cycle-reference") return "cycle stopped";
  if (node.type === "primitive") return `${node.definition.primitive} · ${primitiveSummary(node.definition)}`;
  if (node.type === "transform") {
    return node.operation.transform === "mirror"
      ? `derived mirror · authored source ${node.inputNodeId}`
      : `${node.operation.transform} · input ${node.inputNodeId}`;
  }
  if (node.type === "boolean") return `${node.operation} · ${node.inputNodeIds.length} inputs`;
  return `${node.adapter} · ${node.artifactKey}`;
}

function pathSlug(path: string): string {
  return path.replace(/[^A-Za-z0-9_-]+/g, "-");
}

function flattenHierarchy(
  items: readonly LoomSceneHierarchyItem[],
  collapsedPaths: ReadonlySet<string>,
  parentPath: string | null = null,
): LoomVisibleTreeItem[] {
  const result: LoomVisibleTreeItem[] = [];
  items.forEach((item) => {
    result.push({ item, parentPath });
    if (!collapsedPaths.has(item.path)) {
      result.push(...flattenHierarchy(item.children, collapsedPaths, item.path));
    }
  });
  return result;
}

function TreeItem({
  collapsedPaths,
  focusedPath,
  item,
  onFocusPath,
  onKeyDown,
  onSelect,
  registerRow,
  selectedNodeId,
}: {
  item: LoomSceneHierarchyItem;
  collapsedPaths: ReadonlySet<string>;
  focusedPath: string;
  onFocusPath: (path: string) => void;
  onKeyDown: (event: KeyboardEvent<HTMLLIElement>, item: LoomSceneHierarchyItem) => void;
  onSelect: (nodeId: string) => void;
  registerRow: (path: string, element: HTMLLIElement | null) => void;
  selectedNodeId: string;
}) {
  const owned = item.referenceKind === "owned";
  const selected = owned && item.nodeId === selectedNodeId;
  const targetSelected = item.nodeId === selectedNodeId;
  const expandable = item.children.length > 0;
  const expanded = expandable && !collapsedPaths.has(item.path);
  return (
    <li
      aria-disabled={!item.node || undefined}
      aria-expanded={expandable ? expanded : undefined}
      aria-level={item.depth}
      aria-selected={selected}
      className={selected ? "is-selected" : undefined}
      data-edge-kind={item.edgeKind}
      data-entity-kind={item.entityKind}
      data-first-path={item.firstPath}
      data-node-type={item.node?.type ?? "missing"}
      data-parent-node-id={item.parentNodeId ?? undefined}
      data-reference-kind={item.referenceKind}
      data-reference-node-id={owned ? undefined : item.nodeId}
      data-source-node-id={item.sourceNodeId ?? undefined}
      data-target-selected={String(targetSelected)}
      data-testid={owned
        ? `loom-pattern-treeitem-${item.nodeId}`
        : `loom-pattern-tree-reference-${pathSlug(item.path)}`}
      onFocus={(event) => {
        if (event.target === event.currentTarget) onFocusPath(item.path);
      }}
      onKeyDown={(event) => onKeyDown(event, item)}
      ref={(element) => registerRow(item.path, element)}
      role="treeitem"
      tabIndex={focusedPath === item.path ? 0 : -1}
    >
      <button
        aria-current={selected ? "true" : undefined}
        disabled={!item.node}
        onClick={() => {
          onFocusPath(item.path);
          onSelect(item.nodeId);
        }}
        tabIndex={-1}
        type="button"
      >
        <span><b>{item.node?.label ?? item.nodeId}</b><small>{nodeSummary(item)}</small></span>
        <em>{item.referenceKind === "owned" ? item.edgeKind : "linked"}</em>
      </button>
      {expanded ? (
        <ul role="group">
          {item.children.map((child) => (
            <TreeItem
              collapsedPaths={collapsedPaths}
              focusedPath={focusedPath}
              item={child}
              key={child.key}
              onFocusPath={onFocusPath}
              onKeyDown={onKeyDown}
              onSelect={onSelect}
              registerRow={registerRow}
              selectedNodeId={selectedNodeId}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

export function LoomPatternTree({ document, onSelect, selectedNodeId }: LoomPatternTreeProps) {
  const roots = useMemo(() => deriveLoomSceneHierarchy(document), [document]);
  const [collapsedPaths, setCollapsedPaths] = useState<Set<string>>(() => new Set());
  const selectedPath = useMemo(() => flattenHierarchy(roots, new Set()).find(({ item }) => (
    item.referenceKind === "owned" && item.nodeId === selectedNodeId
  ))?.item.path, [roots, selectedNodeId]);
  const [focusedPath, setFocusedPath] = useState(() => selectedPath ?? roots[0]?.path ?? "");
  const rowRefs = useRef(new Map<string, HTMLLIElement>());
  const visibleItems = useMemo(
    () => flattenHierarchy(roots, collapsedPaths),
    [collapsedPaths, roots],
  );
  const visiblePaths = new Set(visibleItems.map(({ item }) => item.path));
  const effectiveFocusedPath = visiblePaths.has(focusedPath)
    ? focusedPath
    : selectedPath && visiblePaths.has(selectedPath)
      ? selectedPath
      : visibleItems[0]?.item.path ?? "";

  const focusPath = (path: string) => {
    setFocusedPath(path);
    rowRefs.current.get(path)?.focus();
  };
  const setExpanded = (path: string, expanded: boolean) => {
    setCollapsedPaths((current) => {
      const next = new Set(current);
      if (expanded) next.delete(path);
      else next.add(path);
      return next;
    });
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLLIElement>, item: LoomSceneHierarchyItem) => {
    const index = visibleItems.findIndex((entry) => entry.item.path === item.path);
    const entry = visibleItems[index];
    if (!entry) return;
    const expandable = item.children.length > 0;
    const expanded = expandable && !collapsedPaths.has(item.path);
    let handled = true;
    if (event.key === "ArrowDown") focusPath(visibleItems[index + 1]?.item.path ?? item.path);
    else if (event.key === "ArrowUp") focusPath(visibleItems[index - 1]?.item.path ?? item.path);
    else if (event.key === "Home") focusPath(visibleItems[0]?.item.path ?? item.path);
    else if (event.key === "End") focusPath(visibleItems[visibleItems.length - 1]?.item.path ?? item.path);
    else if (event.key === "ArrowRight" && expandable) {
      if (!expanded) setExpanded(item.path, true);
      else focusPath(item.children[0]!.path);
    } else if (event.key === "ArrowLeft") {
      if (expanded) setExpanded(item.path, false);
      else if (entry.parentPath) focusPath(entry.parentPath);
    } else if ((event.key === "Enter" || event.key === " " || event.key === "Spacebar") && item.node) {
      onSelect(item.nodeId);
    } else handled = false;
    if (handled) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  return (
    <ul aria-label="Loom Pattern definition" className="loom-pattern-tree" data-testid="loom-pattern-tree" role="tree">
      {roots.map((root) => (
        <TreeItem
          collapsedPaths={collapsedPaths}
          focusedPath={effectiveFocusedPath}
          item={root}
          key={root.key}
          onFocusPath={focusPath}
          onKeyDown={handleKeyDown}
          onSelect={onSelect}
          registerRow={(path, element) => {
            if (element) rowRefs.current.set(path, element);
            else rowRefs.current.delete(path);
          }}
          selectedNodeId={selectedNodeId}
        />
      ))}
    </ul>
  );
}
